import { useState, useCallback } from 'react'
import { useConnection } from '@solana/wallet-adapter-react'
import { useDagProgram } from './useDagProgram'
import { useAccount } from './useAccount'
import { api } from '~/services/api'
import type { VoteRequest } from '~/types/api'
import { PublicKey } from '@solana/web3.js'
import * as web3 from '@solana/web3.js'
import { BN } from "@coral-xyz/anchor"
import { initializeLightClient } from '../programs/lightClient'
import bs58 from 'bs58'

interface VoteData {
  proposalId: string
  proposalPda: string
  projectPda: string
  userLockPda: string
  voteType: 'approve' | 'delete'
  voteDirection: 'for' | 'against'
  tokenAddress: string
}

interface WorkVoteData {
  workId: string
  workItemPda: string
  proposalPda: string
  projectPda: string
  userLockPda: string
  voteDirection: 'for' | 'against'
  tokenAddress: string
}

interface VoteResult {
  txSignature: string
  proposalId?: string
  workId?: string
  voteType?: 'approve' | 'delete'
  voteDirection: 'for' | 'against'
}

interface TransactionResult {
  txSignature: string
  proposalId?: string
  workId?: string
  voteType?: 'approve' | 'delete'
  voteDirection: 'for' | 'against'
}

export const useVote = () => {
  const { connection } = useConnection()
  const { program, getProjectPda, getUserLockPda } = useDagProgram()
  const { account, isAuthenticated } = useAccount()
  const [isLoading, setIsLoading] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)
  const [isSubmittingVote, setIsSubmittingVote] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [transactionResult, setTransactionResult] = useState<TransactionResult | null>(null)

  const voteOnProposal = useCallback(
    async ({
      projectPda,
      proposalId,
      proposalPda,
      voteDirection,
    }: {
      projectPda: PublicKey;
      proposalId: string;
      proposalPda: string;
      voteDirection: "for" | "against";
    }) => {
      if (!program || !account?.address) {
        throw new Error("Program or account not initialized");
      }

      try {
        console.log("Voting on proposal...");
        const userLockPda = getUserLockPda(account.address, projectPda);
        const proposalPdaKey = new PublicKey(proposalPda);
        const walletPubkey = new PublicKey(account.address);

        // Initialize Light Protocol client
        const lightClient = initializeLightClient(program);

        // Derive the vote address (matching the test pattern exactly)
        const voteAddress = await lightClient.deriveAddressWithSeed([
          Buffer.from("prop_vote"),
          walletPubkey.toBuffer(),
          proposalPdaKey.toBuffer(),
        ]);

        // Get light instruction data for the vote address (using PublicKey array)
        const voteItemInstructionData = await lightClient.getLightInstructionDataForNewAddress([voteAddress.address]);

        // Send the vote transaction (matching test pattern exactly)
        const tx = await program.methods
          .voteOnProposal(
            voteItemInstructionData,
            voteItemInstructionData.outputMerkleTreeIndex,
            voteDirection === "for" // vote_yes
          )
          .accountsStrict({
            project: projectPda,
            proposal: proposalPdaKey,
            owner: walletPubkey,
            userLock: userLockPda,
          })
          .preInstructions([
            web3.ComputeBudgetProgram.setComputeUnitLimit({
              units: 400_000,
            }),
          ])
          .remainingAccounts(voteItemInstructionData.remainingAccounts)
          .rpc();

        console.log("Vote successful:", tx);
        return tx;
      } catch (error) {
        console.error("Voting failed:", error);
        // Check for double-vote error (0x36b4 = 14004)
        if (error instanceof Error && error.message.includes("0x36b4")) {
          throw new Error("You have already voted on this proposal");
        }
        throw error;
      }
    },
    [program, account?.address, getUserLockPda]
  );

  const voteOnWork = async (data: WorkVoteData): Promise<VoteResult> => {
    if (!account?.address) {
      throw new Error('Wallet not connected')
    }

    if (!isAuthenticated) {
      throw new Error('Please sign in first')
    }

    if (!program) {
      throw new Error('Program not initialized')
    }

    setIsLoading(true)
    setError(null)
    setTransactionResult(null)
    setIsConfirming(false)
    setIsSubmittingVote(false)

    try {
      // Step 1: Send transaction and get signature
      console.log('Voting on work on-chain...')
      const tx = await program.methods
        .changeVoteOnWorkItem(
          {}, // light_ix_data
          { approve: {} }, // work_item_vote
          {}, // account_meta
          data.voteDirection === 'for' // vote_yes
        )
        .accountsStrict({
          project: new PublicKey(data.projectPda),
          proposal: new PublicKey(data.proposalPda),
          workItem: new PublicKey(data.workItemPda),
          owner: account.address,
          userLock: new PublicKey(data.userLockPda)
        })
        .rpc()

      console.log('Transaction sent:', {
        workId: data.workId,
        txSignature: tx
      })

      const txResult: TransactionResult = {
        txSignature: tx,
        workId: data.workId,
        voteDirection: data.voteDirection
      }
      setTransactionResult(txResult)

      // Step 2: Submit vote through API
      setIsSubmittingVote(true)
      await submitWorkVoteWithRetry(txResult)
      setIsSubmittingVote(false)

      return {
        txSignature: tx,
        workId: data.workId,
        voteDirection: data.voteDirection
      }
    } catch (err) {
      console.error('Work voting failed:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to vote on work'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setIsLoading(false)
      setIsConfirming(false)
      setIsSubmittingVote(false)
    }
  }

  const submitProposalVoteWithRetry = async (
    result: TransactionResult
  ): Promise<void> => {
    if (!result.proposalId || !result.voteType || !result.voteDirection) {
      throw new Error('Invalid transaction result for proposal vote')
    }

    const voteRequest: VoteRequest = {
      voteType: result.voteType,
      voteDirection: result.voteDirection
    }

    console.log('Submitting vote to API...')
    await api.proposals.vote(result.proposalId, voteRequest)
  }

  const submitWorkVoteWithRetry = async (
    result: TransactionResult
  ): Promise<void> => {
    if (!result.workId || !result.voteDirection) {
      throw new Error('Invalid transaction result for work vote')
    }

    console.log('Submitting work vote to API...')
    await api.works.vote(result.workId, result.voteDirection)
  }

  const retryVoteSubmission = async (): Promise<void> => {
    if (!transactionResult) {
      throw new Error('No transaction result available for retry')
    }

    setIsSubmittingVote(true)
    setError(null)

    try {
      if (transactionResult.proposalId) {
        await submitProposalVoteWithRetry(transactionResult)
      } else if (transactionResult.workId) {
        await submitWorkVoteWithRetry(transactionResult)
      }
    } catch (err) {
      console.error('Vote submission retry failed:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to submit vote'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setIsSubmittingVote(false)
    }
  }

  return {
    voteOnProposal,
    voteOnWork,
    retryVoteSubmission,
    isLoading,
    isConfirming,
    isSubmittingVote,
    error,
    transactionResult,
    clearError: () => setError(null),
  }
} 